<template>
  <v-app-bar
    :elevation="0"
    :height="60"
    :order="0"
    class="app-bar px-2"
    color="white"
    data-locator="app-bar"
  >
    <template v-if="!isSearchOpen">
      <div class="app-bar-layout">
        <div class="app-bar-left column-gap-2">
          <!-- Burger menu for toggling drawer -->
          <app-button
            v-if="fullAccessScope"
            data-locator="app-bar-drawer-toggle"
            type="icon"
            @click="onDrawerToggleClick"
          >
            <app-material-icon name="menu" />
          </app-button>

          <!-- Logo in AppBar -->
          <router-link
            v-if="fullAccessScope"
            :to="{ name: routes.ROOMS_ROUTE_NAME }"
            class="d-flex align-center"
            data-locator="back-to-dashboard-button"
          >
            <img
              :src="appBarLogo"
              class="app-bar-logo"
              alt="Logo"
              data-locator="app-bar-logo"
            />
          </router-link>
          <template v-else>
            <v-img
              alt="Closd Name"
              src="@/assets/img/closd_logo.svg"
              :width="$vuetify.display.mobile ? 72 : 100"
            />
          </template>
        </div>
        <div v-if="fullAccessScope" class="app-bar-center">
          <AppBarDocumentSearch v-if="!$vuetify.display.xs && shouldDisplayGlobalSearch" />
        </div>

        <div class="app-bar-right column-gap-1">
          <app-button
            v-if="$vuetify.display.xs && shouldDisplayGlobalSearch"
            type='icon'
            @click="isSearchOpen = true"
          >
            <app-material-icon name="search" />
          </app-button>

          <ProjectsMenuItem v-if="fullAccessScope" />

          <AppTooltip v-if="!$vuetify.display.mobile">
            <template v-slot:activator="{ props }">
              <app-button
                v-if="fullAccessScope && profile.subscription && !profile.subscription?.hideArchiveOption"
                type="icon"
                :class="{ '--is-active': currentRoute.name.includes(ARCHIVES_ROUTE_NAME) }"
                v-bind="props"
                @click="onArchivesClick"
              >
                <ArchiveAddAccessIcon :isArchivingEnabled="isArchivingEnabled" :solid="currentRoute.name.includes(ARCHIVES_ROUTE_NAME)"/>
              </app-button>
            </template>
            {{ $t('common.app-bar.AppBar.archiving') }}
          </AppTooltip>

          <HelpMenuItem v-if="!$vuetify.display.mobile"/>

          <v-divider
            v-if="fullAccessScope"
            class="mx-1 my-4 bg-grey-200"
            vertical
          />

          <ProfileMenuItem :fullAccessScope="fullAccessScope"/>
        </div>
      </div>
    </template>

    <template v-if="shouldDisplayGlobalSearch && isSearchOpen">
      <app-button
        class="mx-3"
        type='icon'
        size="small"
        @click="isSearchOpen = false"
      >
        <app-icon-ds icon="arrow-left" size="sm" />
      </app-button>
      <AppBarDocumentSearch />
    </template>

  </v-app-bar>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'

import closdLogo from '@/assets/img/closd_logo.svg'
import AppTooltip from '@/common/AppTooltip.vue'
import ArchiveAddAccessIcon from '@/common/archives/ArchiveAddAccessIcon.vue'
import { getRoomNavigationDataFromRoute } from '@/common/navigation-drawer/roomNavigationData.js'
import { getRoomIcon } from '@/common/utils/rooms'
import {
  HOME_LAYOUT_ROUTE_NAMES,
  ROOMS_ROUTE_NAME,
  ROOM_ROUTE_TYPES,
  PROFILE_ROUTE_NAME,
  SUBSCRIPTION_ROUTE_NAME,
  ARCHIVES_ROUTE_NAME,
  ARCHIVES_ADD_ROUTE_NAME,
} from '@/router/routerData'
import { getCurrentRouteSafe } from '@/router/routerInstance'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'
import { useArchivesStore } from '@/stores/archivesStore'

import AppBarDocumentSearch from './AppBarDocumentSearch.vue'
import HelpMenuItem from './HelpMenuItem.vue'
import ProfileMenuItem from './ProfileMenuItem.vue'
import ProjectsMenuItem from './ProjectsMenuItem.vue'

export default {
  name: 'AppBar',
  components: {
    AppBarDocumentSearch,
    AppTooltip,
    ArchiveAddAccessIcon,
    HelpMenuItem,
    ProfileMenuItem,
    ProjectsMenuItem,
  },
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  props: {
    fullAccessScope: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isSearchOpen: false,
      ARCHIVES_ROUTE_NAME,
      tab: 1,
      item: 0,
      discoverDialogIsOpen: false,
    }
  },
  computed: {
    ...mapState('global', ['homeLayout', 'projectLayout', 'mini', 'navigationDrawerProfile', 'toggleProjectDrawer']),
    ...mapState('documents', ['searchFilesPending']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['roomName']),
    ...mapGetters('user', ['isArchivingEnabled']),
    ...mapState('user', ['profile']),
    ...mapState('documentsBreadcrumb', ['breadcrumb']),
    ...mapGetters('subscription', ['getSubscriptionLogo']),
    // We use this one because we are init even before the router is properly finished,
    // since the app can deduce the layout before then and we are at the top level
    currentRoute () {
      return getCurrentRouteSafe()
    },
    currentRoomNavigationPage () {
      return getRoomNavigationDataFromRoute(this.currentRoute)
    },
    shouldDisplayRoomBreadcrumb () {
      return this.currentRoom && !!this.currentRoomNavigationPage && this.currentRoute.name !== ROOMS_ROUTE_NAME
    },
    hasSubscriptionLogo () {
      return !!this.getSubscriptionLogo
    },
    appBarLogo () {
      return this.getSubscriptionLogo || closdLogo
    },
    shouldDisplayGlobalSearch () {
      const { name, params } = this.currentRoute
      return (
        HOME_LAYOUT_ROUTE_NAMES.includes(name) ||
        [ROOM_ROUTE_TYPES.PROJECT, ROOM_ROUTE_TYPES.DATAROOM].includes(params.roomType)
      )
    },
    routes () {
      return {
        ROOMS_ROUTE_NAME,
      }
    },
    isInProfileLayout() {
      return this.currentRoute?.name?.includes(PROFILE_ROUTE_NAME)
    },
    isInSubscriptionLayout() {
      return this.currentRoute?.name?.includes(SUBSCRIPTION_ROUTE_NAME)
    },
    isInArchivesLayout() {
      return [ARCHIVES_ROUTE_NAME, ARCHIVES_ADD_ROUTE_NAME].includes(this.currentRoute?.name)
    },
    projectCategoryIcon() {
      let src = ''
      if (this.projectLayout && this.currentRoom) {
        src = getRoomIcon(this.currentRoom)
      }
      return src
    },
    computedMini: {
      get () {
        return this.mini
      },
      set (val) {
        this.$store.commit(`global/${GlobalMutationTypes.SET_MINI}`, val)
      },
    },
  },
  created() {
    this.ROOMS_ROUTE_NAME = ROOMS_ROUTE_NAME
    this.ARCHIVES_ROUTE_NAME = ARCHIVES_ROUTE_NAME
  },
  methods: {
    ...mapMutations('global', [
      GlobalMutationTypes.SET_TOGGLE_PROJECT_DRAWER,
      GlobalMutationTypes.SET_NAVIGATION_DRAWER_PROFILE,
    ]),
    onDrawerToggleClick () {
      if (this.$vuetify.display.mobile) {
        if (this.isInProfileLayout || this.isInSubscriptionLayout || this.isInArchivesLayout) {
          this.SET_NAVIGATION_DRAWER_PROFILE(!this.navigationDrawerProfile)
          return
        }

        this.SET_TOGGLE_PROJECT_DRAWER(!this.toggleProjectDrawer)
      } else {
        this.computedMini = !this.computedMini
      }
    },
    openProfileDrawer () {
      this.$store.commit('global/' + GlobalMutationTypes.SET_NAVIGATION_DRAWER_PROFILE, true)
    },
    onArchivesClick () {
      if (this.isArchivingEnabled) {
        this.$router.push({ name: ARCHIVES_ROUTE_NAME })
      } else {
        this.archivesStore.setDiscoverElectronicSafeDialogIsOpen(true)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.app-bar {
  border-bottom: 1px solid rgb(var(--v-theme-grey-100));
}

.app-bar-layout {
  display: flex;
  align-items: center;
  justify-content:space-between;
  gap: 12px;
  width: 100%;
  height: 100%;
}

.app-bar-left {
  display: flex;
  align-items: center;
}

.app-bar-logo {
  display: block;
  height: 24px;
  max-width: 140px;
  width: auto;
}

.app-bar-center {
  flex: 0 1 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}

.app-bar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
}

:deep(.v-toolbar__content) {
  overflow: visible;

  .v-btn:first-child {
    margin-inline-start: 0px;
  }
}
</style>
